import { Check, Copy } from 'lucide-react'
import { useState } from 'react'
import { clsx } from 'clsx'
import { useTranslation } from '@/shared/i18n/useTranslation'
import type { SurvivorGameDetail } from '../types'
import { SurvivorGameStatus } from '../types'
import styles from './GameStatusCard.module.css'

/**
 * The game's vitals as a key/value strip under a rule, not a card of tiles.
 * Everything here is a number or a state word, which is exactly what the
 * departures-board treatment is for.
 */
export function GameStatusCard({ game }: { game: SurvivorGameDetail }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  async function handleCopyCode() {
    try {
      await navigator.clipboard.writeText(game.joinCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch { /* clipboard may be unavailable */ }
  }

  const statusLabel =
    game.status === SurvivorGameStatus.RegistrationOpen ? t('survivor.status.waiting')
    : game.status === SurvivorGameStatus.Active         ? t('survivor.status.inProgress')
                                                        : t('survivor.status.finished')

  const myStatus = game.isChampion ? 'champion' : game.isAlive ? 'alive' : 'eliminated'
  const myStatusLabel =
    myStatus === 'champion' ? t('survivor.status.champion')
    : myStatus === 'alive'  ? t('survivor.status.alive')
                            : t('survivor.status.eliminated')

  return (
    <section className={styles.card}>
      <span className={styles.competitionName}>
        {game.competitionName}
        {/* Only worth the pixels once the game has actually been restarted — a game on its
            first season is just "the game", and every game would otherwise read "Temporada 1". */}
        {game.seasonNumber > 1 && (
          <span className={styles.seasonTag}>
            {t('survivor.season.short', { number: game.seasonNumber })}
          </span>
        )}
      </span>

      <div className={styles.strip}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>{t('survivor.game.state')}</span>
          <span className={styles.statValue}>{statusLabel}</span>
        </div>

        <div className={styles.stat}>
          <span className={styles.statLabel}>{t('survivor.game.aliveCount')}</span>
          <span className={clsx(styles.statValue, styles.isNumeric)}>
            {game.aliveCount}
            <span className={styles.statOf}>/{game.participantCount}</span>
          </span>
        </div>

        <div className={styles.stat}>
          <span className={styles.statLabel}>{t('survivor.game.you')}</span>
          <span className={clsx(styles.statValue, styles[`my_${myStatus}`])}>{myStatusLabel}</span>
        </div>
      </div>

      <button
        type="button"
        className={styles.codeRow}
        onClick={handleCopyCode}
        aria-label={t('survivor.game.copyCode', { code: game.joinCode })}
      >
        <span className={styles.codeLabel}>{t('survivor.game.code')}</span>
        <span className={styles.codeValue}>{game.joinCode}</span>
        {copied ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
      </button>
    </section>
  )
}
